import { computed } from 'vue';

import {
  useSession,
  useToast,
  useAuctionEvent,
  openActivationGate,
  formatCents,
} from '@/shell';
import { useBidStore } from '../store';

/**
 * usePlaceBid — composable wiring the bid feature's submit flow:
 *
 *   1. If the bidder isn't activated, open the shell activation gate.
 *      Resume the bid only after activation succeeds.
 *   2. Submit via the bid store.
 *   3. On success, fire a toast (B4.2) and let the page redirect. A same-max
 *      collision (`store.tieResult` set — the earlier bidder wins ties, so
 *      this submission is immediately outbid) gets outbid-flavored copy
 *      instead of the plain success toast, since every current caller
 *      confirms a placement by toast rather than an in-sheet panel.
 *
 * Caller drives navigation; the composable doesn't push routes itself
 * because the page already owns that policy. It also doesn't reach into
 * the my-bids store directly (that's a feature-to-feature import features/
 * may not make) — callers live in shell/components, so they call
 * `useMyBidsStore().noteOwnPlacement(...)` themselves on a successful
 * `place()` to re-sync the Winning/Outbid buckets from the server instead
 * of depending on a single WS frame (stale-"Outbid"-after-rebid fix).
 *
 * `withErrorToast` exists for surfaces that already render the store's
 * error inline (InlineBidForm's warn Message) — a toast on top would
 * double-signal the same failure.
 */
export function usePlaceBid(options?: { withToast?: boolean; withErrorToast?: boolean }) {
  const withToast = options?.withToast ?? true;
  const withErrorToast = options?.withErrorToast ?? withToast;
  const session = useSession();
  const toast = useToast();
  const store = useBidStore();

  async function place(): Promise<boolean> {
    if (!session.isAuthenticated) {
      toast.warn({ summary: 'Sign in to bid', detail: 'You need an account to place a bid.' });
      return false;
    }
    if (!session.isActivated) {
      const activated = await openActivationGate();
      if (!activated) return false;
    }
    let ok = await store.submit();

    // TERMS_NOT_ACCEPTED means the auction gained terms after this bidder
    // activated, so they are activated but have not accepted (GT1-529 —
    // activation latches, and it gates bidding only). The server is the only
    // thing that can tell us: auctionEvent memoizes per slug and carries no
    // frame for a terms edit, so a session loaded before the edit still holds
    // an event with no terms and would open a drawer with nothing to accept.
    // Force the reload FIRST, then reopen the gate, then retry once.
    if (!ok && store.errorCode === 'TERMS_NOT_ACCEPTED') {
      const slug = useAuctionEvent().slug;
      if (slug) await useAuctionEvent().loadBySlug(slug, true);
      const accepted = await openActivationGate();
      if (!accepted) return false;
      ok = await store.submit();
    }

    if (ok) {
      // Toasts fire imperatively from the instance that placed the bid —
      // BidSheet and InlineBidForm are both mounted and both share the bid
      // store, so a store-state watcher here would double-toast every
      // placement.
      if (withToast) {
        if (store.tieResult) {
          // The engine breaks a same-max collision in favor of the earlier
          // bidder — this submission tied and lost, so it's an outbid, not
          // a win, even though the bid itself was accepted.
          toast.warn({
            summary: "You've been outbid",
            detail: `Tied at ${formatCents(store.tieResult.tiedAtCents)}. An earlier bid wins ties. Raise to ${formatCents(store.tieResult.suggestedNextCents)} to take the lead.`,
          });
        } else {
          toast.success({ summary: 'Bid placed', detail: 'Your bid is in.' });
        }
      }
    } else if (withErrorToast && store.errorMessage) {
      toast.danger({ summary: 'Bid not placed', detail: store.errorMessage });
    }
    return ok;
  }

  return {
    place,
    submitting: computed(() => store.state === 'submitting'),
    success: computed(() => store.state === 'success'),
    errorMessage: computed(() => store.errorMessage),
    tieResult: computed(() => store.tieResult),
  };
}
